export interface SchemaEventPayload {
    uri: string;
    data: Record<string, unknown>;
}

const URI_DOMAIN = 'der:tailormade';
const URI_CONTEXT = 'productinfo';
const MFE_NAME = String(import.meta.env?.VITE_MFE_NAME ?? 'der-tm-productinfo').replace('@dertour/', '');

/**
 * Checks if the Schema.org listener is ready to receive events.
 *
 * @returns boolean
 */
function isSchemaOrgListenerReady(): boolean {
    return (window as unknown as { schemaInjectorReady?: boolean }).schemaInjectorReady === true;
}

/**
 * Dispatches a custom event with the raw payload for productinfo schema.
 *
 * @param payload SchemaEventPayload The typed payload to be dispatched.
 * @param dispatchTarget Optional event target for dispatching the schema event.
 */
export function triggerSchemaJsonEvent(payload: SchemaEventPayload, dispatchTarget: EventTarget = window): void {
    const event = new CustomEvent('der-tm:schema', {
        detail: payload,
        bubbles: true,
        composed: true,
    });

    if (isSchemaOrgListenerReady()) {
        dispatchTarget.dispatchEvent(event);
    } else {
        const retryDelay = 500; // ms
        const maxRetries = 10;
        let attempts = 0;

        const checkAndDispatch = () => {
            if (isSchemaOrgListenerReady()) {
                dispatchTarget.dispatchEvent(event);
            } else if (attempts < maxRetries) {
                attempts++;
                setTimeout(checkAndDispatch, retryDelay);
            }
        };

        setTimeout(checkAndDispatch, retryDelay);
    }
}

/**
 * Set an initial URI for the given id by combining it with the predefined domain, context, and MFE name.
 * @param id string
 * @returns string
 */
export function setInitialUri(id: string): string {
    return `${URI_DOMAIN}:${URI_CONTEXT}:${MFE_NAME}:${id}`;
}
